import { CardKind, OfferKind } from "./entities";
import { HandPlan, planHand } from "./handPlan";
import { handSupply, paceCounters, paceIntervals, paceStep } from "./systems/draftFlow";
import type { LevelSim } from "./levelSim";

/**
 * Read-only card-pacing numbers for the HUD (DESIGN §4.4): how far away the next tower cards and upgrade card are, how
 * many points the next offer still needs, and how much a pick pushes it back. Pure reads of sim state — asking never
 * changes a replay or the hash. They read the same hand plans the sim deals (handPlan.planHand), so they count only
 * offers that will show — an in-run offer with nothing due, or a lone card with nothing to borrow, is passed over
 * without showing, and the HUD skips it too — and a tower or Upgrade card borrowed ahead of its pace counts where it
 * will be dealt.
 */

/** Looked-ahead offers past which the HUD stops counting (every pace falls due well inside it). */
const LOOKAHEAD_OFFERS = 32;

function openInRunOfferShows(sim: LevelSim, kind: CardKind): boolean {
  const open = sim.currentOffer;
  return open !== null && open.kind === OfferKind.PostWave && open.cards.some((c) => c.kind === kind);
}

/**
 * The next in-run offers as the pacing will deal them, read at the towers standing and the road laid now (placing a
 * tower or a road piece can move them). `visit` gets each offer's hand plan (null: it does not show); returning true
 * stops.
 */
export function lookAhead(sim: LevelSim, visit: (plan: HandPlan | null, index: number) => boolean): void {
  const cards = sim.balance.cards, intervals = paceIntervals(sim), supply = handSupply(sim);
  let at = paceCounters(sim);
  for (let i = 0; i < LOOKAHEAD_OFFERS; i++) {
    const step = paceStep(cards, at, intervals.towers, intervals.road);
    if (visit(planHand(cards, step, intervals, supply), i)) return;
    at = step.next;
  }
}

/** Offers shown until (and including) the first whose hand `holds` a card of the kind; 0 when `openShows`. */
function shownUntil(sim: LevelSim, openShows: boolean, holds: (p: HandPlan) => boolean): number {
  if (openShows) return 0;
  let shown = 0, result = LOOKAHEAD_OFFERS;
  lookAhead(sim, (plan) => {
    if (plan === null) return false;
    shown++;
    if (holds(plan)) { result = shown; return true; }
    return false;
  });
  return result;
}

/**
 * In-run offers until one carries tower cards: 0 when the open in-run offer shows them, 1 when the next shown offer
 * will, and so on. Counted at the towers standing now (placing one lengthens the wait).
 */
export function offersUntilTowers(sim: LevelSim): number {
  return shownUntil(sim, openInRunOfferShows(sim, CardKind.Tower), (p) => p.towers > 0);
}

/** In-run offers until one carries an Upgrade card (0 when the open in-run offer shows one). */
export function offersUntilUpgrade(sim: LevelSim): number {
  return shownUntil(sim, openInRunOfferShows(sim, CardKind.Upgrade), (p) => p.upgrade);
}

/**
 * Points a pick of this kind adds to the next offer's threshold when placed from an in-run offer (the opening draft
 * charges nothing). The number to print on the card: "+40 pts to next offer".
 */
export function pickDelay(sim: LevelSim, kind: CardKind): number {
  const open = sim.currentOffer;
  if (open !== null && open.kind !== OfferKind.PostWave) return 0;
  const cost = sim.balance.cards.pickPointsCost;
  switch (kind) {
    case CardKind.Path: return cost.path;
    case CardKind.Tower: return cost.tower;
    case CardKind.Upgrade: return cost.upgrade;
    case CardKind.Move: return sim.balance.cards.moveCard.pointsCost;
    default: return 0;
  }
}

/**
 * The points total at which the next offer that will show opens: `nextOfferAt`, or a later threshold when the ones
 * before it have nothing due (they pass without showing). What the NEXT CARD pill counts down to.
 */
export function nextShownOfferAt(sim: LevelSim): number {
  let at = sim.nextOfferAt;
  lookAhead(sim, (plan, i) => {
    if (i > 0) at = sim.balance.cards.offerThreshold(sim.offersOpened + 1 + i);
    return plan !== null;
  });
  return at;
}

/** Kill points still needed before the next in-run offer that shows (0 while one is open or due). */
export function pointsToNextOffer(sim: LevelSim): number {
  return Math.max(0, nextShownOfferAt(sim) - sim.points);
}
